

import { BorderBox } from '@/components/atoms/BorderBox';
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetOrganisationCrmList } from "@/features/crm/hooks/useQuery/useGetOrganisationCrmList";
import { useCreateCrmProject } from '@/features/crm_project/hooks/useMutation/useCreateCrmProject';
import { ProjectConfigurationParameterInput } from '@/features/parameters/components/atoms/ProjectConfigurationParameterInput';
import { selectedConfigurationSchema } from '@/lib/schema/configuration/configuration';
import { matchConfigurationData, matchConfigurationDataWithDefault, matchDefaultConfigurationData } from "@/lib/utils/crm/matchConfigurationData";
import { Configuration } from "@/types/crm/configuration/configuration";
import { zodResolver } from "@hookform/resolvers/zod";
import { DialogDescription, DialogTrigger } from "@radix-ui/react-dialog";
import { Loader } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";

export function CreateCrmProjectModal() {
    const [showModal, setShowModal] = useState(false);
    const onClose = () => setShowModal(false);

    const { orgId } = useParams()
    const { data: orgCrmData, isLoading: isLoadingOrgCrmsByOrgId } = useGetOrganisationCrmList(Number(orgId));

    const [selectedCrmData, setSelectedCrmData] = useState<string | undefined>();
    const crm = useMemo(() => {
        if (!orgCrmData || !selectedCrmData) return undefined;
        return orgCrmData?.find(c => c.id === Number(selectedCrmData));
    }, [orgCrmData, selectedCrmData]);

    const { mutateAsync: createCrmProject, isPending } = useCreateCrmProject();
    const schema = z.object({
        name: z.string().min(1, { message: 'Name is required' }),
        description: z.string().optional(),
        selected_configuration: selectedConfigurationSchema.optional(),
    });
    type schemaType = z.infer<typeof schema>;

    const form = useForm<schemaType>({
        resolver: zodResolver(schema),
        defaultValues: {
            name: undefined
        }
    });
    const onSubmit = async (data: schemaType) => {
        if (!selectedCrmData) {
            throw new Error("CRM Project Configuration id is required");
        }

        createCrmProject({ ...data, crm_id: Number(selectedCrmData) })
            .then(() => {
                SuccessPopup("Crm Project Configuration created successfully");
                onClose();
            }).catch(() => {
                ErrorPopup("Something went wrong");
            })
    };


    useEffect(() => {
        if (crm) {
            form.reset({
                name: undefined,
                description: undefined,
                selected_configuration: {
                    parameter: matchConfigurationDataWithDefault(crm)?.parameter?.project?.map(p => ({
                        parameter_id: p.id,
                        name: p.name,
                        value: p.value,
                    })) || []
                },
            })
        }
    }, [showModal, form, crm])
    const matchedConfiguration = useMemo(() => {
        if (!crm) return undefined;
        return matchConfigurationData(crm, crm.configuration as Configuration);
    }, [crm]);
    const matchDefaultConfiguration = useMemo(() => {
        if (!crm) return undefined;
        return matchDefaultConfigurationData(crm);
    }, [crm]);
    return (
        <Dialog open={showModal} onOpenChange={setShowModal} defaultOpen={false} >
            <DialogTrigger asChild>
                <Button size="sm">
                    Add CRM Project Configuration for projects
                </Button>
            </DialogTrigger>

            <DialogContent >
                <DialogHeader>
                    <DialogTitle className=''>
                        <div className="p-0 m-0 font-bold text-start">
                            Add CRM Project Configuration
                        </div>
                    </DialogTitle>
                    <DialogDescription>
                        Choose the CRM Configuration used as a basis for this project configuration.
                    </DialogDescription>
                </DialogHeader>
                <hr />
                <ScrollArea className="max-h-[60vh] sm:max-h-[70vh]">
                    <div className="flex flex-col gap-2 p-1 mb-3">

                        <Label>Select CRM Configuration *</Label>
                        <Select
                            onValueChange={setSelectedCrmData}
                            value={selectedCrmData}
                            disabled={isLoadingOrgCrmsByOrgId}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select CRM Configuration" />
                            </SelectTrigger>
                            <SelectContent>
                                {orgCrmData?.map((crm) => (
                                    <SelectItem key={crm?.id} value={crm?.id ? String(crm?.id) : ""}>
                                        <div className="flex flex-col items-start justify-start text-start">
                                            <p className="">{crm?.name}</p>
                                            <p className="text-xs capitalize">CRM System: <span className="font-semibold">{crm?.crm_system}</span></p>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
                                <FormField
                                    control={form.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>CRM Project Name *</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>CRM Project Description</FormLabel>
                                            <FormControl>
                                                <Input {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {/* <ParametersForm
                                    label="Default Parameters"
                                    parametersFields={defaultParametersFields}
                                    form={form}
                                    parametersObj={crm?.configuration_data?.parameter?.project || {}}
                                    defaultParameter
                                /> */}
                                {(matchDefaultConfiguration?.parameter?.project || []).length > 0 && (
                                    <Card className="rounded-md">
                                        <CardContent className="px-4 pt-4">
                                            <CardHeader className="p-0 mb-4">
                                                <CardTitle>Default Parameters</CardTitle>
                                            </CardHeader>
                                            <ProjectConfigurationParameterInput
                                                crmSystem={crm?.crm_system}
                                                fullConfiguration={matchDefaultConfiguration}
                                                parameters={matchDefaultConfiguration?.parameter?.project || []}
                                                form={form}
                                            />
                                        </CardContent>
                                    </Card>
                                )}
                                {(matchedConfiguration?.parameter?.project || []).length > 0 && (
                                    <Card className="rounded-md">
                                        <CardContent className="px-4 pt-4">
                                            <CardHeader className="p-0 mb-4">
                                                <CardTitle>Custom Parameters</CardTitle>
                                            </CardHeader>
                                            <ProjectConfigurationParameterInput
                                                crmSystem={crm?.crm_system}
                                                fullConfiguration={matchedConfiguration}
                                                parameters={matchedConfiguration?.parameter?.project || []}
                                                form={form}
                                            />
                                        </CardContent>
                                    </Card>
                                )}

                                {/* <ParametersForm
                                    label="Custom Parameters"
                                    parametersFields={parametersFields}
                                    form={form}
                                    parametersObj={crm?.configuration_data?.parameter?.project || {}}
                                /> */}
                            </form>
                        </Form>

                        <BorderBox variant='info' size='sm'>
                            <div>
                                If you want to add more CRM configurations, <Link to={`/organization/${orgId}/manage-organization`} className="font-semibold">click here.</Link>
                            </div>
                        </BorderBox>
                    </div>
                </ScrollArea>
                <hr />
                <DialogFooter>
                    <div className="flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                        <Button onClick={() => {
                            form.handleSubmit(onSubmit)()
                        }}
                            disabled={!selectedCrmData || isPending || Object.keys(form?.formState.errors || {}).length > 0}
                        >
                            {isPending && <Loader className="w-4 h-4 animate-spin" />}
                            Save
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog >
    )
}
